import React, { useEffect, useState } from "react";
import { Check, Copy, Maximize2 } from "lucide-react";

import { ListCard } from "@/shared/components/ListCard";
import { IconButton } from "@/shared/components/ui-kit";

import { lazyChunk, useChunk } from "./lazyChunk";

/* The one code block: an answer's fenced code, a skill's frontmatter and raw SKILL.md, and
   the frame of a drawn diagram — the app's card in a tray (`ListCard`): the tray's strip names
   the language (or a title) and carries the icon buttons, the white card holds the code (or what
   it draws), which scrolls sideways on its own.
   Highlighting arrives with the first block on a page (its chunk loads once, 22–31 KB
   gzipped); until then, and for a language it doesn't know, the code shows plain.

   The highlighted code is highlight.js's HTML, the one HTML an agent's code turns into: it
   escapes every character it was given and adds only `hljs-*` spans. Ceiling: a block being
   streamed is highlighted again on each update, linear in its length. */

const highlighter = lazyChunk(() => import("./highlight"));

type CodeBlockProps = {
  code: string;
  lang?: string;
  /** what the strip says in place of the language */
  title?: React.ReactNode;
  /** what the code draws, in place of the code */
  children?: React.ReactNode;
  /** opens what it draws full size: an Expand button on the strip */
  onExpand?: () => void;
};

export default function CodeBlock({ code, lang = "", title, children, onExpand }: CodeBlockProps) {
  const name = title ?? (lang || "Code");
  return (
    <ListCard
      label={typeof name === "string" ? <span title={name}>{name}</span> : name}
      action={
        <span className="flex items-center gap-0.5">
          {onExpand && (
            <IconButton type="button" aria-label="Expand" title="Expand" onClick={onExpand}>
              <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            </IconButton>
          )}
          <CopyButton text={code} />
        </span>
      }
    >
      {children ? <div className="p-4 overflow-x-auto">{children}</div> : <Code code={code} lang={lang} />}
    </ListCard>
  );
}

function Code({ code, lang }: { code: string; lang: string }) {
  const highlight = useChunk(highlighter)?.highlight;
  const html = lang && highlight ? highlight(code, lang) : null;
  return (
    <pre className="m-0 px-4 py-3.5 overflow-x-auto font-mono text-[12.5px] leading-[21px] text-[var(--text-primary)] whitespace-pre">
      {html === null ? <code>{code}</code> : <code className="code-highlight" dangerouslySetInnerHTML={{ __html: html }} />}
    </pre>
  );
}

/** Copies `text`, a `--success` check for 1.5s after; named for what it copies. */
export function CopyButton({ text, label = "Copy code" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = () => navigator.clipboard?.writeText(text).then(() => setCopied(true), () => undefined);
  return (
    <IconButton type="button" aria-label={copied ? "Copied" : label} title={copied ? "Copied" : label} onClick={copy}>
      {copied ? <Check className="w-3.5 h-3.5 text-[var(--success)]" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />}
    </IconButton>
  );
}
